import type {
  AgentChatSessionSummaryContract,
  AgentReferenceType,
  CompanyWorkspace,
  PrivateAsset,
  WorkspaceContract,
} from '@nest-react/domain';
import type { AppLocale } from '@nest-react/lib';

import type { AssetUploadItem } from '~/features/assets/uploadQueue';

export type AssetMutationError =
  | 'deleteForbidden'
  | 'deleteInUse'
  | 'deleteMissing'
  | 'deleteFailed'
  | 'favoriteFailed';

export type WorkspaceAssetsSnapshot = {
  assets: PrivateAsset[];
  urls: Record<string, string>;
  uploads: AssetUploadItem[];
  loading: boolean;
  loadingMore: boolean;
  loadMoreError: string | null;
  error: string | null;
  mutationError: AssetMutationError | null;
  nextCursor: string | null;
};

export const emptyWorkspaceAssetsSnapshot = (): WorkspaceAssetsSnapshot => ({
  assets: [],
  urls: {},
  uploads: [],
  loading: false,
  loadingMore: false,
  loadMoreError: null,
  error: null,
  mutationError: null,
  nextCursor: null,
});

export type WorkspaceAgentSessionListSnapshot = {
  items: AgentChatSessionSummaryContract[];
  loading: boolean;
  loadingMore: boolean;
  loadMoreError: boolean;
  loaded: boolean;
  nextCursor: string | null;
};

export const emptyAgentSessionListSnapshot =
  (): WorkspaceAgentSessionListSnapshot => ({
    items: [],
    loading: false,
    loadingMore: false,
    loadMoreError: false,
    loaded: false,
    nextCursor: null,
  });

export type WorkspaceAgentReferenceItem = {
  id: string;
  assetId: string;
  previewUrl: string;
  type: 'IMAGE';
  name: string;
  referenceName: string;
  referenceId: string;
  referenceType: AgentReferenceType;
  uploadStatus: 'ready';
};

export type WorkspaceAgentReferencesSnapshot = {
  items: WorkspaceAgentReferenceItem[];
  loading: boolean;
  loaded: boolean;
};

export const emptyAgentReferencesSnapshot =
  (): WorkspaceAgentReferencesSnapshot => ({
    items: [],
    loading: false,
    loaded: false,
  });

export type WorkspaceBillingSnapshot =
  import('./billingTypes').WorkspaceBillingSnapshot;
export {
  emptyBillingSnapshot,
  emptyBillingActivitySnapshot,
  billingActivityRequestKey,
  type WorkspaceBillingActivitySnapshot,
} from './billingTypes';

export type WorkspaceIdentitySnapshot = {
  activeWorkspaceId: string | null;
  workspaceContracts: WorkspaceContract[];
  companies: CompanyWorkspace[];
  locale: AppLocale;
};

export const emptyIdentitySnapshot = (): WorkspaceIdentitySnapshot => ({
  activeWorkspaceId: null,
  workspaceContracts: [],
  companies: [],
  locale: 'en',
});
